import { comuneByIstat } from '@sse/gazetteer';
import { normalizePlace } from '@sse/gazetteer/normalize';
import { CATEGORIES, type Event, type EventDate, type Link as EventLink } from '@sse/schema';
import {
  ArrowLeft,
  CalendarPlus,
  ExternalLink,
  LocateFixed,
  Lock,
  MapPinned,
  Megaphone,
  Merge,
  Plus,
  Trash2,
  Unlock,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Link,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router';
import { ComunePicker } from '../components/comune-picker';
import { EventStats } from '../components/event-stats';
import { ImageCard } from '../components/image-card';
import {
  Card,
  Field,
  StatusBadge,
  btnGhost,
  btnPrimary,
  btnSecondary,
  inputBaseCls,
  inputCls,
} from '../components/ui';
import { ApiError, type EventDetail, type EventReport } from '../lib/api';
import {
  REPORT_TOPICS,
  FIELD_LABELS,
  STATUS_HINTS,
  STATUS_LABELS,
  categoryLabel,
  formatRange,
  formatStamp,
  todayRome,
} from '../lib/format';
import { useSession } from '../lib/session';

type Category = Event['category'];
type Status = Event['status'];

/** Stato del form: campi piatti e stringhe vuote al posto dei campi assenti. */
interface Form {
  title: string;
  category: Category;
  secondaryCategories: Category[];
  tags: string[];
  comuneId: string;
  frazione: string;
  venueName: string;
  venueAddress: string;
  geo: Event['geo'] | null;
  dates: EventDate[];
  description: string;
  shortDesc: string;
  seoTitle: string;
  seoDescription: string;
  links: EventLink[];
  featuredUntil: string;
  /** edizioni sul sito: '' automatico, 'none' mai collegato, altrimenti nome della serie */
  seriesId: string;
  audience: '' | 'kids' | 'family' | 'none';
  /** chi si esibisce: slug dell'anagrafica degli artisti */
  artists: string[];
  status: Status;
  slug: string;
}

const toForm = (e: Event): Form => ({
  title: e.title,
  category: e.category,
  secondaryCategories: e.secondaryCategories ?? [],
  tags: e.tags ?? [],
  comuneId: e.comuneId,
  frazione: e.frazione ?? '',
  venueName: e.venue?.name ?? '',
  venueAddress: e.venue?.address ?? '',
  geo: e.geo,
  dates: e.dates,
  description: e.description,
  shortDesc: e.shortDesc,
  seoTitle: e.seo?.title ?? '',
  seoDescription: e.seo?.description ?? '',
  links: e.links ?? [],
  featuredUntil: e.featured?.until ?? '',
  seriesId: e.seriesId ?? '',
  audience: e.audience ?? '',
  artists: e.artists ?? [],
  status: e.status,
  slug: e.slug,
});

const emptyForm = (): Form => ({
  title: '',
  category: 'SAGRE',
  secondaryCategories: [],
  tags: [],
  comuneId: '',
  frazione: '',
  venueName: '',
  venueAddress: '',
  geo: null,
  dates: [{ date: todayRome() }],
  description: '',
  shortDesc: '',
  seoTitle: '',
  seoDescription: '',
  links: [],
  featuredUntil: '',
  seriesId: '',
  audience: '',
  artists: [],
  status: 'draft',
  slug: '',
});

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const sortDates = (d: EventDate[]) =>
  [...d]
    .filter((x) => x.date)
    .map((x) => ({
      date: x.date,
      ...(x.start ? { start: x.start } : {}),
      ...(x.end ? { end: x.end } : {}),
    }))
    .sort((a, b) => a.date.localeCompare(b.date));

/** Valori del form nel formato dell'API (null = togli il campo). */
function toPayload(f: Form) {
  const venue =
    f.venueName.trim() || f.venueAddress.trim()
      ? {
          ...(f.venueName.trim() ? { name: f.venueName.trim() } : {}),
          ...(f.venueAddress.trim() ? { address: f.venueAddress.trim() } : {}),
        }
      : null;
  return {
    title: f.title.trim(),
    category: f.category,
    secondaryCategories: f.secondaryCategories.filter((c) => c !== f.category),
    tags: f.tags,
    comuneId: f.comuneId,
    frazione: f.frazione.trim() || null,
    venue,
    geo: f.geo,
    dates: sortDates(f.dates),
    description: f.description,
    shortDesc: f.shortDesc.trim(),
    seo:
      f.seoTitle.trim() || f.seoDescription.trim()
        ? { title: f.seoTitle.trim(), description: f.seoDescription.trim() }
        : null,
    links: f.links
      .filter((l) => l.url.trim())
      .map((l) => ({
        ...l,
        url: l.url.trim(),
        ...(l.label?.trim() ? { label: l.label.trim() } : {}),
      })),
    featured: f.featuredUntil ? { until: f.featuredUntil } : null,
    seriesId: f.seriesId.trim() || null,
    audience: f.audience || null,
    artists: f.artists.length ? f.artists : null,
    status: f.status,
    slug: f.slug.trim(),
  };
}

/** Solo i campi cambiati rispetto all'evento salvato. */
function diff(f: Form, original: Event): Record<string, unknown> {
  const now = toPayload(f);
  const before = toPayload(toForm(original));
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(now) as Array<keyof typeof now>)
    if (!same(now[k], before[k])) out[k] = now[k];
  return out;
}

/** Centro della frazione (se nota) o del comune. */
function centerOf(comuneId: string, frazione: string): Event['geo'] | null {
  const c = comuneByIstat(comuneId);
  if (!c) return null;
  const fr = frazione
    ? c.frazioni.find((x) => normalizePlace(x.name) === normalizePlace(frazione))
    : undefined;
  if (fr?.lat !== undefined && fr.lng !== undefined)
    return { lat: fr.lat, lng: fr.lng, precision: 'frazione' };
  return { ...c.centroid, precision: 'comune' };
}

const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T12:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};

/** Scheda di un evento: modifica (con :id) o inserimento (events/new). */
export function EventEditor() {
  const { id } = useParams();
  // arrivati da "Problemi segnalati": promemoria del problema e ritorno alla risposta
  const [query] = useSearchParams();
  const reportId = query.get('report');
  const navState = useLocation().state as { report?: EventReport; merged?: string } | null;
  const fromReport = navState?.report;
  const isNew = !id;
  const { api, config, canEdit, isAdmin } = useSession();
  const [deleted, setDeleted] = useState(false);
  const navigate = useNavigate();
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const [form, setForm] = useState<Form>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error' | 'conflict'; text: string } | null>(
    navState?.merged
      ? {
          kind: 'ok',
          text: `Eventi uniti: «${navState.merged}» è stato eliminato e il suo indirizzo porta qui.`,
        }
      : null,
  );
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (isNew) {
      setDetail(null);
      setForm(emptyForm());
      return;
    }
    api<EventDetail>(`/events/${id}`).then(
      (d) => {
        setDetail(d);
        setForm(toForm(d.event));
      },
      (e) => {
        // unito a un altro evento: si apre quello
        if (e instanceof ApiError && e.body.mergedInto)
          return navigate(`/events/${e.body.mergedInto}`, { replace: true });
        setLoadError(e instanceof ApiError && e.status === 404 ? 'Evento non trovato.' : String(e));
      },
    );
  }, [id, isNew, api]);

  const original = detail?.event;
  const changes = useMemo(() => (original ? diff(form, original) : {}), [form, original]);
  const dirty = isNew ? form.title !== '' || form.comuneId !== '' : Object.keys(changes).length > 0;

  // modifiche non salvate: conferma prima di uscire (navigazione interna e chiusura della scheda)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty && !saving && !deleted && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('Ci sono modifiche non salvate. Uscire senza salvare?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const showIssues = (e: ApiError) => {
    const map: Record<string, string> = {};
    for (const i of e.body.issues ?? []) {
      const key = i.path.split('.')[0] || '_';
      map[key] ??= i.message;
    }
    setErrors(map);
    setNotice({
      kind: 'error',
      text: `Controlla i campi evidenziati${e.body.issues?.length ? '' : `: ${e.message}`}.`,
    });
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setErrors({});
    setNotice(null);
    try {
      if (isNew) {
        const p = toPayload(form);
        const body = Object.fromEntries(
          Object.entries(p).filter(
            ([k, v]) =>
              k !== 'slug' && v !== null && !(Array.isArray(v) && v.length === 0) && v !== '',
          ),
        );
        if (!form.geo) delete body.geo;
        const { event } = await api<{ event: Event }>('/events', { method: 'POST', body });
        setForm(toForm(event));
        setDetail({ event, audit: [], sources: [] });
        navigate(`/events/${event.id}`, { replace: true });
        setNotice({ kind: 'ok', text: 'Evento creato.' });
      } else if (original) {
        const { event } = await api<{ event: Event }>(`/events/${original.id}`, {
          method: 'PATCH',
          body: { version: original.version, changes },
        });
        const d = await api<EventDetail>(`/events/${event.id}`);
        setDetail(d);
        setForm(toForm(d.event));
        setNotice({ kind: 'ok', text: 'Modifiche salvate. Il sito si aggiorna entro un minuto.' });
      }
    } catch (e) {
      if (e instanceof ApiError && e.body.error === 'version_conflict')
        setNotice({
          kind: 'conflict',
          text: 'L’evento è stato modificato da qualcun altro (o dalla pipeline) mentre lo stavi modificando.',
        });
      else if (e instanceof ApiError && e.body.error === 'slug_taken') {
        setErrors({ slug: 'Questo indirizzo è già usato da un altro evento.' });
        setNotice({ kind: 'error', text: 'Indirizzo (slug) già in uso.' });
      } else if (e instanceof ApiError && e.status === 400) showIssues(e);
      else
        setNotice({
          kind: 'error',
          text: `Salvataggio non riuscito: ${e instanceof Error ? e.message : String(e)}`,
        });
    } finally {
      setSaving(false);
    }
  };

  // Ctrl/Cmd+S
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        if (dirty) void save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const reload = async () => {
    if (!id) return;
    const d = await api<EventDetail>(`/events/${id}`);
    setDetail(d);
    setForm(toForm(d.event));
    setNotice(null);
    setErrors({});
  };

  const unlock = async (field: string) => {
    if (!original) return;
    try {
      const { event } = await api<{ event: Event }>(`/events/${original.id}/unlock`, {
        method: 'POST',
        body: { version: original.version, fields: [field] },
      });
      // le modifiche in corso nel form restano: cambiano solo versione e campi bloccati
      setDetail((d) => (d ? { ...d, event } : d));
    } catch (e) {
      setNotice({
        kind: 'error',
        text:
          e instanceof ApiError && e.body.error === 'version_conflict'
            ? 'Versione cambiata: ricarica l’evento.'
            : String(e),
      });
    }
  };

  if (!canEdit) return <main className="p-6 text-muted">Serve il ruolo editor o admin.</main>;
  if (loadError)
    return (
      <main className="p-6">
        <p>{loadError}</p>
        <Link to="/events" className="mt-4 inline-block underline">
          Torna agli eventi
        </Link>
      </main>
    );
  if (!isNew && !detail) return <main className="p-6 text-muted">Caricamento…</main>;

  const comune = form.comuneId ? comuneByIstat(form.comuneId) : undefined;
  const publicUrl =
    original && original.status !== 'draft' ? `${config.siteUrl}/events/${original.slug}` : null;
  const locked = new Set(original?.lockedFields ?? []);
  // i campi bloccati (modificati a mano) hanno il lucchetto nell'etichetta
  const label = (text: string, field: string): string => text + (locked.has(field) ? ' 🔒' : '');

  return (
    <main className="mx-auto max-w-[1400px] p-4 pb-28 md:p-6 md:pb-28">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/events" className={btnGhost}>
          <ArrowLeft className="size-4" aria-hidden /> Eventi
        </Link>
        {original && <StatusBadge status={original.status} />}
        {original && (
          <span className="text-sm text-muted">
            {formatRange(original.startDate, original.endDate)}
          </span>
        )}
        {original && original.status === 'published' && (
          <Link to={`/social?event=${original.id}`} className={`${btnGhost} ml-auto`}>
            <Megaphone className="size-4" aria-hidden /> Programma sui social
          </Link>
        )}
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noopener" className={btnGhost}>
            <ExternalLink className="size-4" aria-hidden /> Vedi sul sito
          </a>
        )}
      </div>
      <h1 className="mt-2 font-display text-h2">{isNew ? 'Nuovo evento' : original?.title}</h1>

      {reportId && (
        <div className="mt-4 flex flex-wrap items-center gap-3 border-l-4 border-fg bg-accent-soft p-3 text-sm">
          <span className="flex-1">
            <strong className="font-medium">Problema segnalato</strong>
            {fromReport && (
              <>
                {' '}
                ({REPORT_TOPICS[fromReport.topic] ?? fromReport.topic}): «{fromReport.note}»
              </>
            )}
            . Salva le correzioni, poi torna alla segnalazione per rispondere.
          </span>
          <Link to={`/reports?id=${reportId}&outcome=corrected`} className={btnSecondary}>
            Torna alla segnalazione
          </Link>
        </div>
      )}

      {notice && (
        <div
          role={notice.kind === 'ok' ? 'status' : 'alert'}
          className={`mt-4 flex flex-wrap items-center gap-3 p-3 text-sm ${notice.kind === 'ok' ? 'bg-ok-soft text-fg' : 'bg-danger-soft'}`}
        >
          <span className="flex-1">{notice.text}</span>
          {notice.kind === 'conflict' && (
            <button onClick={reload} className={btnSecondary}>
              Ricarica l’ultima versione (perdi le modifiche)
            </button>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex flex-col gap-4">
          <Card title="Contenuto">
            <Field label={label('Titolo', 'title')} error={errors.title}>
              <input
                className={inputCls}
                value={form.title}
                maxLength={200}
                onChange={(e) => set('title', e.target.value)}
              />
            </Field>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={label('Categoria', 'category')} error={errors.category}>
                <select
                  className={inputCls}
                  value={form.category}
                  onChange={(e) => set('category', e.target.value as Category)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {categoryLabel(c)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={label('Tag', 'tags')}
                hint="Invio o virgola per aggiungere"
                error={errors.tags}
              >
                <TagsInput value={form.tags} onChange={(v) => set('tags', v)} />
              </Field>
            </div>
            <Field
              label={label('Altre categorie', 'secondaryCategories')}
              error={errors.secondaryCategories}
            >
              <div className="flex flex-wrap gap-1">
                {CATEGORIES.filter((c) => c !== form.category).map((c) => {
                  const on = form.secondaryCategories.includes(c);
                  return (
                    <button
                      type="button"
                      key={c}
                      aria-pressed={on}
                      onClick={() =>
                        set(
                          'secondaryCategories',
                          on
                            ? form.secondaryCategories.filter((x) => x !== c)
                            : [...form.secondaryCategories, c],
                        )
                      }
                      className={`h-8 px-2 text-xs ${on ? 'bg-fg text-page' : 'bg-surface hover:bg-surface-strong'}`}
                    >
                      {categoryLabel(c)}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field
              label={label('Descrizione breve', 'shortDesc')}
              hint={`${form.shortDesc.length}/140 · nelle card e nei risultati`}
              error={errors.shortDesc}
            >
              <input
                className={inputCls}
                value={form.shortDesc}
                maxLength={140}
                onChange={(e) => set('shortDesc', e.target.value)}
              />
            </Field>
            <MarkdownField
              label={label('Descrizione', 'description')}
              value={form.description}
              onChange={(v) => set('description', v)}
              error={errors.description}
            />
          </Card>

          <Card
            title={label('Date e orari', 'dates')}
            actions={
              <span className="text-xs text-muted">
                {form.dates.length === 1 ? '1 giorno' : `${form.dates.length} giorni`}
              </span>
            }
          >
            <DatesEditor value={form.dates} onChange={(v) => set('dates', v)} />
            {errors.dates && <p className="text-xs font-medium text-danger">{errors.dates}</p>}
          </Card>

          <Card title="Luogo">
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={label('Comune', 'comuneId')} error={errors.comuneId}>
                <ComunePicker
                  value={form.comuneId || undefined}
                  onChange={(v) => set('comuneId', v ?? '')}
                />
              </Field>
              <Field
                label={label('Frazione', 'frazione')}
                hint="Facoltativa"
                error={errors.frazione}
              >
                <input
                  className={inputCls}
                  list="frazioni"
                  value={form.frazione}
                  onChange={(e) => set('frazione', e.target.value)}
                />
                <datalist id="frazioni">
                  {comune?.frazioni.map((f) => (
                    <option key={f.slug} value={f.name} />
                  ))}
                </datalist>
              </Field>
              <Field
                label={label('Nome del luogo', 'venue')}
                hint="Es. Piazza Sant’Oronzo, Castello"
                error={errors.venue}
              >
                <input
                  className={inputCls}
                  value={form.venueName}
                  onChange={(e) => set('venueName', e.target.value)}
                />
              </Field>
              <Field label="Indirizzo" error={errors.venue}>
                <input
                  className={inputCls}
                  value={form.venueAddress}
                  onChange={(e) => set('venueAddress', e.target.value)}
                />
              </Field>
            </div>
            <GeoEditor
              value={form.geo}
              onChange={(g) => set('geo', g)}
              center={() => centerOf(form.comuneId, form.frazione)}
              locate={
                form.comuneId && (form.venueName.trim() || form.venueAddress.trim())
                  ? async () => {
                      const r = await api<{ lat: number; lng: number; label: string }>(
                        '/events/geocode',
                        {
                          method: 'POST',
                          body: {
                            comuneId: form.comuneId,
                            ...(form.frazione.trim() ? { frazione: form.frazione.trim() } : {}),
                            ...(form.venueName.trim() ? { venueName: form.venueName.trim() } : {}),
                            ...(form.venueAddress.trim()
                              ? { address: form.venueAddress.trim() }
                              : {}),
                          },
                        },
                      );
                      set('geo', { lat: r.lat, lng: r.lng, precision: 'venue' });
                      return r.label;
                    }
                  : undefined
              }
              locked={locked.has('geo')}
              error={errors.geo}
            />
          </Card>

          <Card title={label('Link', 'links')}>
            <LinksEditor value={form.links} onChange={(v) => set('links', v)} />
            {errors.links && <p className="text-xs font-medium text-danger">{errors.links}</p>}
          </Card>

          <Card title="SEO (facoltativo)">
            <p className="text-xs text-muted">
              Se vuoti, titolo e descrizione per Google si generano dall’evento. Servono entrambi.
            </p>
            <Field label={`Titolo SEO · ${form.seoTitle.length}/70`} error={errors.seo}>
              <input
                className={inputCls}
                value={form.seoTitle}
                maxLength={70}
                onChange={(e) => set('seoTitle', e.target.value)}
              />
            </Field>
            <Field label={`Descrizione SEO · ${form.seoDescription.length}/170`}>
              <textarea
                className={`${inputCls} h-20 py-2`}
                value={form.seoDescription}
                maxLength={170}
                onChange={(e) => set('seoDescription', e.target.value)}
              />
            </Field>
          </Card>
        </div>

        {/* colonna laterale */}
        <aside className="flex flex-col gap-4">
          <Card title="Pubblicazione">
            <Field
              label={label('Stato', 'status')}
              hint={STATUS_HINTS[form.status]}
              error={errors.status}
            >
              <select
                className={inputCls}
                value={form.status}
                onChange={(e) => set('status', e.target.value as Status)}
              >
                {(['published', 'draft', 'cancelled', 'archived'] as Status[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="In evidenza fino al"
              hint="Vuoto: non in evidenza"
              error={errors.featured}
            >
              <input
                type="date"
                className={inputCls}
                value={form.featuredUntil}
                onChange={(e) => set('featuredUntil', e.target.value)}
              />
            </Field>
            <Field
              label="Per famiglie"
              hint={
                form.audience
                  ? 'Scelto qui: l’AI non lo cambia.'
                  : 'Lo decide l’AI dopo la pubblicazione (per bambini, per famiglie o nessuno dei due).'
              }
            >
              <select
                className={`${inputCls} max-w-[280px]`}
                value={form.audience}
                onChange={(e) => set('audience', e.target.value as Form['audience'])}
              >
                <option value="">Da valutare (AI)</option>
                <option value="kids">Per bambini</option>
                <option value="family">Per famiglie</option>
                <option value="none">Né per bambini né per famiglie</option>
              </select>
            </Field>
            <Field
              label={label('Chi si esibisce', 'artists')}
              hint={
                locked.has('artists')
                  ? 'Scelti qui: l’AI non li cambia più.'
                  : 'Li legge l’AI dopo la pubblicazione e li collega all’anagrafica. Se li cambi qui, l’AI non li tocca più.'
              }
              error={errors.artists}
            >
              <ArtistsInput value={form.artists} onChange={(v) => set('artists', v)} />
            </Field>
            <Field
              label="Altre edizioni sul sito"
              hint={
                form.seriesId === 'none'
                  ? 'Questo evento non viene collegato ad altri.'
                  : form.seriesId.trim()
                    ? 'Collegato agli eventi con lo stesso nome di serie, oltre a quelli trovati da soli.'
                    : 'Automatico: stesso comune e stesso titolo senza anno né numero di edizione. Scrivi un nome per collegarlo ad altri eventi con lo stesso nome.'
              }
            >
              <div className="flex flex-wrap items-center gap-3">
                <input
                  className={`${inputCls} max-w-[280px]`}
                  value={form.seriesId === 'none' ? '' : form.seriesId}
                  disabled={form.seriesId === 'none'}
                  placeholder="Automatico"
                  onChange={(e) => set('seriesId', e.target.value)}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.seriesId === 'none'}
                    onChange={(e) => set('seriesId', e.target.checked ? 'none' : '')}
                  />
                  Non collegare
                </label>
              </div>
            </Field>
            {!isNew && (
              <Field
                label="Indirizzo (slug)"
                hint={
                  form.slug !== original?.slug
                    ? 'Il vecchio indirizzo farà redirect al nuovo.'
                    : `/events/${form.slug}`
                }
                error={errors.slug}
              >
                <input
                  className={inputCls}
                  value={form.slug}
                  onChange={(e) => set('slug', e.target.value.toLowerCase())}
                />
              </Field>
            )}
          </Card>

          {original && (
            <Card title="Doppione">
              <p className="text-sm text-muted">
                Lo stesso evento è sul sito due volte? Uniscilo con l’altro: questo resta (con il
                suo indirizzo), scegli campo per campo cosa tenere e l’altro sparisce.
              </p>
              <div>
                <Link to={`/events/merge?target=${original.id}`} className={btnSecondary}>
                  <Merge className="size-4" aria-hidden /> Unisci con un altro evento
                </Link>
              </div>
            </Card>
          )}

          {original && isAdmin && (
            <Card title="Eliminazione">
              <p className="text-sm text-muted">
                Per toglierlo dal sito basta lo stato <b>Bozza</b>. L’eliminazione è definitiva:
                scheda, storico e statistiche spariscono, l’indirizzo risponde 404 e la sorgente da
                cui era arrivato non lo riproporrà più.
              </p>
              <div>
                <button
                  type="button"
                  disabled={deleted}
                  className={`${btnSecondary} border-danger text-danger`}
                  onClick={() => {
                    const typed = window.prompt(
                      `Eliminare definitivamente «${original.title}»?\n\nNon si può annullare. Scrivi ELIMINA per confermare.`,
                    );
                    if (typed?.trim().toUpperCase() !== 'ELIMINA') return;
                    setDeleted(true);
                    api(`/events/${original.id}?version=${original.version}`, { method: 'DELETE' })
                      .then(() => navigate('/events', { replace: true }))
                      .catch((e: unknown) => {
                        setDeleted(false);
                        setNotice({
                          kind: 'error',
                          text:
                            e instanceof ApiError && e.body.error === 'version_conflict'
                              ? 'L’evento è cambiato nel frattempo: ricarica la pagina e riprova.'
                              : `Eliminazione non riuscita: ${String(e)}`,
                        });
                      });
                  }}
                >
                  <Trash2 className="size-4" aria-hidden /> Elimina evento
                </button>
              </div>
            </Card>
          )}

          {original ? (
            <ImageCard
              event={original}
              onChange={(ev) => {
                // le modifiche in corso nel form restano: cambiano evento salvato e storico
                setDetail((d) => (d ? { ...d, event: ev } : d));
                void api<EventDetail>(`/events/${ev.id}`).then(setDetail);
              }}
            />
          ) : (
            <Card title="Immagine o locandina">
              <p className="text-sm text-muted">
                Crea l’evento per aggiungere la locandina o una foto.
              </p>
            </Card>
          )}

          {original && original.lockedFields.length > 0 && (
            <Card title="Campi bloccati">
              <p className="text-xs text-muted">
                Modificati a mano: gli import automatici non li sovrascrivono. Sbloccali per
                lasciarli aggiornare dalle sorgenti.
              </p>
              <ul className="flex flex-wrap gap-1">
                {original.lockedFields.map((f) => (
                  <li key={f}>
                    <button
                      type="button"
                      onClick={() => unlock(f)}
                      className="inline-flex h-8 items-center gap-1 bg-surface px-2 text-xs hover:bg-surface-strong"
                      title="Sblocca"
                    >
                      <Lock className="size-3" aria-hidden /> {FIELD_LABELS[f] ?? f}{' '}
                      <Unlock className="size-3 text-muted" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {original && original.status !== 'draft' && <EventStats eventId={original.id} />}

          {original && (
            <Card title="Informazioni">
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-xs [&_dd]:break-all">
                <dt className="text-muted">ID</dt>
                <dd className="font-mono break-all">{original.id}</dd>
                <dt className="text-muted">Creato</dt>
                <dd>{formatStamp(original.createdAt)}</dd>
                <dt className="text-muted">Aggiornato</dt>
                <dd>{formatStamp(original.updatedAt)}</dd>
                <dt className="text-muted">Versione</dt>
                <dd>{original.version}</dd>
                <dt className="text-muted">Sorgenti</dt>
                <dd>
                  {detail?.sources.length
                    ? detail.sources.map((s) => s.ref).join(', ')
                    : original.legacyIds.length
                      ? 'sito v1'
                      : 'inserito a mano'}
                </dd>
              </dl>
            </Card>
          )}

          {detail && detail.audit.length > 0 && (
            <Card title="Storico">
              <ol className="flex flex-col gap-2 text-xs">
                {detail.audit.map((a) => (
                  <li key={a.at + a.action} className="border-l-2 border-surface-strong pl-2">
                    <span className="font-medium">{formatStamp(a.at)}</span> ·{' '}
                    {a.by.sub === 'auto' ? 'pubblicazione automatica' : (a.by.email ?? a.by.sub)}
                    <br />
                    <span className="text-muted">
                      {a.merge
                        ? `unito con «${a.merge.title}»`
                        : a.action === 'create'
                          ? 'creato'
                          : a.action === 'unlock'
                            ? 'campi sbloccati'
                            : Object.keys(a.changes)
                                .filter((k) => k !== 'startDate' && k !== 'endDate')
                                .map((k) => FIELD_LABELS[k] ?? k)
                                .join(', ')}
                    </span>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </aside>
      </div>

      {/* barra di salvataggio */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-surface-strong bg-page/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-3 md:px-6">
          <span className="text-sm text-muted">
            {isNew
              ? 'Nuovo evento: verrà creato come indicato in "Stato".'
              : dirty
                ? `Modifiche non salvate: ${Object.keys(changes)
                    .map((k) => FIELD_LABELS[k] ?? k)
                    .join(', ')}`
                : 'Nessuna modifica'}
          </span>
          <div className="ml-auto flex gap-2">
            {!isNew && dirty && (
              <button
                type="button"
                onClick={() => original && setForm(toForm(original))}
                className={btnSecondary}
              >
                Annulla modifiche
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={!dirty || saving}
              className={btnPrimary}
              title="Ctrl/Cmd+S"
            >
              {saving ? 'Salvataggio…' : isNew ? 'Crea evento' : 'Salva'}
            </button>
          </div>
        </div>
      </div>
    </main>
  );
}

interface ArtistOption {
  slug: string;
  name: string;
  kind: string;
}

/**
 * Artisti dell'evento: nomi dall'anagrafica (`/admin/booking/artists`), si tolgono con la × e si
 * aggiungono cercando per nome.
 */
function ArtistsInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const { api } = useSession();
  const [names, setNames] = useState<Record<string, string>>({});
  const [text, setText] = useState('');
  const [options, setOptions] = useState<ArtistOption[]>([]);

  // nomi degli artisti già collegati
  useEffect(() => {
    const missing = value.filter((s) => !(s in names));
    if (!missing.length) return;
    let alive = true;
    void Promise.all(
      missing.map((s) =>
        api<ArtistOption>(`/booking/artists/${s}`)
          .then((a) => [s, a.name] as const)
          .catch(() => [s, s] as const),
      ),
    ).then((pairs) => alive && setNames((n) => ({ ...n, ...Object.fromEntries(pairs) })));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // ricerca per nome
  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) return setOptions([]);
    let alive = true;
    const t = setTimeout(() => {
      api<{ artists: ArtistOption[] }>(`/booking/artists?q=${encodeURIComponent(q)}`)
        .then(
          (r) => alive && setOptions(r.artists.filter((a) => !value.includes(a.slug)).slice(0, 8)),
        )
        .catch(() => alive && setOptions([]));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-h-10 flex-wrap items-center gap-1 border border-fg/25 bg-page px-1 py-1">
        {value.map((s) => (
          <span key={s} className="inline-flex items-center gap-1 bg-surface px-2 py-0.5 text-xs">
            {names[s] ?? s}
            <button
              type="button"
              aria-label={`Togli ${names[s] ?? s}`}
              onClick={() => onChange(value.filter((x) => x !== s))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          className="h-8 min-w-32 flex-1 px-1 text-sm outline-none"
          value={text}
          placeholder="Cerca un artista…"
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      {options.length > 0 && (
        <ul className="border border-fg/15 bg-page text-sm">
          {options.map((a) => (
            <li key={a.slug}>
              <button
                type="button"
                className="w-full px-2 py-1 text-left hover:bg-surface"
                onClick={() => {
                  setNames((n) => ({ ...n, [a.slug]: a.name }));
                  onChange([...value, a.slug]);
                  setText('');
                  setOptions([]);
                }}
              >
                {a.name} <span className="text-fg/60">· {a.kind}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TagsInput({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    const t = text.trim().replace(/,$/, '').toLowerCase();
    if (t && !value.includes(t)) onChange([...value, t]);
    setText('');
  };
  return (
    <div className="flex min-h-10 flex-wrap items-center gap-1 border border-fg/25 bg-page px-1 py-1">
      {value.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 bg-surface px-2 py-0.5 text-xs">
          {t}
          <button
            type="button"
            aria-label={`Togli ${t}`}
            onClick={() => onChange(value.filter((x) => x !== t))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="h-8 min-w-24 flex-1 px-1 text-sm outline-none"
        value={text}
        onChange={(e) =>
          e.target.value.endsWith(',') ? (setText(e.target.value), add()) : setText(e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !text && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add}
      />
    </div>
  );
}

function MarkdownField({
  label,
  value,
  onChange,
  error,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string | undefined;
}) {
  const [preview, setPreview] = useState(false);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-label font-medium uppercase tracking-[0.04em] text-muted">
          {label}
        </span>
        <div className="flex text-xs">
          <button
            type="button"
            onClick={() => setPreview(false)}
            className={`px-2 py-1 ${!preview ? 'bg-fg text-page' : 'bg-surface'}`}
          >
            Testo
          </button>
          <button
            type="button"
            onClick={() => setPreview(true)}
            className={`px-2 py-1 ${preview ? 'bg-fg text-page' : 'bg-surface'}`}
          >
            Anteprima
          </button>
        </div>
      </div>
      {preview ? (
        <div className="prose-admin min-h-48 border border-fg/25 p-3 text-sm">
          <Markdown remarkPlugins={[remarkGfm]}>{value || '_Nessuna descrizione_'}</Markdown>
        </div>
      ) : (
        <textarea
          className={`${inputCls} h-64 py-2 font-mono text-[13px]`}
          value={value}
          maxLength={10000}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
      <span className="mt-1 block text-xs text-muted">
        Markdown: **grassetto**, _corsivo_, - elenchi, [link](https://…) · {value.length}/10000
      </span>
      {error && <span className="mt-1 block text-xs font-medium text-danger">{error}</span>}
    </div>
  );
}

function DatesEditor({
  value,
  onChange,
}: {
  value: EventDate[];
  onChange: (v: EventDate[]) => void;
}) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const update = (i: number, patch: Partial<EventDate>) =>
    onChange(
      value.map((d, j) =>
        j === i
          ? (Object.fromEntries(
              Object.entries({ ...d, ...patch }).filter(([, v]) => v),
            ) as EventDate)
          : d,
      ),
    );
  const addRange = () => {
    if (!from) return;
    const end = to && to >= from ? to : from;
    const days: EventDate[] = [];
    for (let d = from; d <= end && days.length < 366; d = addDays(d, 1)) days.push({ date: d });
    const existing = new Set(value.map((d) => d.date));
    onChange(sortDates([...value, ...days.filter((d) => !existing.has(d.date))]));
    setFrom('');
    setTo('');
  };
  return (
    <div className="flex flex-col gap-2">
      <div className="hidden grid-cols-[minmax(0,1fr)_7rem_7rem_2.5rem] gap-2 text-label uppercase tracking-[0.04em] text-muted sm:grid">
        <span>Giorno</span>
        <span>Dalle</span>
        <span>Alle</span>
        <span />
      </div>
      {value.map((d, i) => (
        // telefono: il giorno su una riga, sotto gli orari e il cestino
        <div
          key={i}
          className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_2.5rem] gap-2 border-b border-surface-strong pb-2 sm:grid-cols-[minmax(0,1fr)_7rem_7rem_2.5rem] sm:border-0 sm:pb-0"
        >
          <input
            type="date"
            className={`${inputCls} col-span-3 sm:col-span-1`}
            value={d.date}
            onChange={(e) => update(i, { date: e.target.value })}
            aria-label="Giorno"
          />
          <input
            type="time"
            className={inputCls}
            value={d.start ?? ''}
            onChange={(e) => update(i, { start: e.target.value })}
            aria-label="Dalle"
          />
          <input
            type="time"
            className={inputCls}
            value={d.end ?? ''}
            onChange={(e) => update(i, { end: e.target.value })}
            aria-label="Alle"
          />
          <button
            type="button"
            className={btnGhost}
            disabled={value.length === 1}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            aria-label="Togli il giorno"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </div>
      ))}
      <div className="flex flex-wrap items-end gap-2 border-t border-surface-strong pt-3">
        <button
          type="button"
          className={btnSecondary}
          onClick={() => {
            const last = sortDates(value).at(-1)?.date ?? todayRome();
            const prev = value.find((x) => x.date === last);
            onChange([
              ...value,
              {
                date: addDays(last, 1),
                ...(prev?.start ? { start: prev.start } : {}),
                ...(prev?.end ? { end: prev.end } : {}),
              },
            ]);
          }}
        >
          <Plus className="size-4" aria-hidden /> Giorno dopo
        </button>
        <span className="text-xs text-muted">oppure un intervallo:</span>
        <input
          type="date"
          className={`${inputBaseCls} w-40`}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Dal"
        />
        <input
          type="date"
          className={`${inputBaseCls} w-40`}
          value={to}
          min={from}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Al"
        />
        <button type="button" className={btnSecondary} onClick={addRange} disabled={!from}>
          <CalendarPlus className="size-4" aria-hidden /> Aggiungi
        </button>
        <button
          type="button"
          className={`${btnGhost} ml-auto`}
          onClick={() => onChange(sortDates(value))}
        >
          Ordina
        </button>
      </div>
    </div>
  );
}

function GeoEditor({
  value,
  onChange,
  center,
  locate,
  locked,
  error,
}: {
  value: Event['geo'] | null;
  onChange: (g: Event['geo'] | null) => void;
  center: () => Event['geo'] | null;
  /** posizione dal nome del luogo o dall'indirizzo (Amazon Location); restituisce cosa ha trovato */
  locate?: (() => Promise<string>) | undefined;
  locked: boolean;
  error?: string | undefined;
}): ReactNode {
  const num = (s: string) => (s === '' ? NaN : Number(s));
  const [finding, setFinding] = useState(false);
  const [found, setFound] = useState<string | null>(null);
  const find = async () => {
    if (!locate) return;
    setFinding(true);
    setFound(null);
    try {
      setFound(`Trovato: ${await locate()}`);
    } catch (e) {
      setFound(e instanceof ApiError ? e.message : String(e));
    } finally {
      setFinding(false);
    }
  };
  const ok = value && Number.isFinite(value.lat) && Number.isFinite(value.lng);
  return (
    <div className="grid items-end gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
      <Field label={`Latitudine${locked ? ' 🔒' : ''}`} error={error}>
        <input
          className={inputCls}
          inputMode="decimal"
          value={value?.lat ?? ''}
          onChange={(e) =>
            onChange({ lat: num(e.target.value), lng: value?.lng ?? NaN, precision: 'venue' })
          }
        />
      </Field>
      <Field label="Longitudine">
        <input
          className={inputCls}
          inputMode="decimal"
          value={value?.lng ?? ''}
          onChange={(e) =>
            onChange({ lat: value?.lat ?? NaN, lng: num(e.target.value), precision: 'venue' })
          }
        />
      </Field>
      <Field label="Precisione">
        <select
          className={inputCls}
          value={value?.precision ?? 'comune'}
          onChange={(e) =>
            value && onChange({ ...value, precision: e.target.value as Event['geo']['precision'] })
          }
        >
          <option value="venue">Luogo esatto</option>
          <option value="frazione">Frazione</option>
          <option value="comune">Comune</option>
        </select>
      </Field>
      <button type="button" className={btnSecondary} onClick={() => onChange(center())}>
        <LocateFixed className="size-4" aria-hidden /> Centro del luogo
      </button>
      <div className="flex flex-wrap items-center gap-3 md:col-span-4">
        <button
          type="button"
          className={btnSecondary}
          disabled={!locate || finding}
          onClick={() => void find()}
          title={
            locate
              ? 'Cerca le coordinate dal nome del luogo o dall’indirizzo'
              : 'Scrivi comune e nome del luogo o indirizzo'
          }
        >
          <MapPinned className="size-4" aria-hidden />{' '}
          {finding ? 'Ricerca…' : 'Trova dal luogo o dall’indirizzo'}
        </button>
        {ok && (
          <a
            href={`https://www.openstreetmap.org/?mlat=${value.lat}&mlon=${value.lng}#map=18/${value.lat}/${value.lng}`}
            target="_blank"
            rel="noopener"
            className="text-sm underline"
          >
            Controlla sulla mappa
          </a>
        )}
        {found && <span className="text-sm text-muted">{found}</span>}
      </div>
      <p className="text-xs text-muted md:col-span-4">
        Con "Comune" o "Frazione" la mappa mostra la posizione come indicativa. Cambiando comune la
        posizione si sposta da sola, tranne se è un luogo esatto. Gli import cercano da soli la
        posizione quando la sorgente indica il luogo.
      </p>
    </div>
  );
}

const LINK_TYPES: Array<{ id: EventLink['type']; label: string }> = [
  { id: 'official', label: 'Sito ufficiale' },
  { id: 'tickets', label: 'Biglietti' },
  { id: 'social', label: 'Social' },
  { id: 'source', label: 'Fonte' },
  { id: 'other', label: 'Altro' },
];

function LinksEditor({
  value,
  onChange,
}: {
  value: EventLink[];
  onChange: (v: EventLink[]) => void;
}) {
  const update = (i: number, patch: Partial<EventLink>) =>
    onChange(value.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  return (
    <div className="flex flex-col gap-2">
      {value.map((l, i) => (
        <div key={i} className="grid gap-2 md:grid-cols-[10rem_2fr_1fr_2.5rem]">
          <select
            className={inputCls}
            value={l.type}
            onChange={(e) => update(i, { type: e.target.value as EventLink['type'] })}
            aria-label="Tipo"
          >
            {LINK_TYPES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            className={inputCls}
            placeholder="https://…"
            value={l.url}
            onChange={(e) => update(i, { url: e.target.value })}
            aria-label="URL"
          />
          <input
            className={inputCls}
            placeholder="Etichetta (facoltativa)"
            value={l.label ?? ''}
            onChange={(e) => update(i, { label: e.target.value })}
            aria-label="Etichetta"
          />
          <button
            type="button"
            className={btnGhost}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
            aria-label="Togli il link"
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        </div>
      ))}
      <button
        type="button"
        className={`${btnSecondary} self-start`}
        onClick={() => onChange([...value, { type: 'official', url: '' }])}
      >
        <Plus className="size-4" aria-hidden /> Aggiungi link
      </button>
    </div>
  );
}
